/* eslint-disable react/no-children-prop */
/* eslint-disable react/prop-types */
/* eslint-disable no-underscore-dangle */
import React from 'react';

import { Header, Grid, Label, Button, Breadcrumb, Card } from 'semantic-ui-react';

import { graphql } from 'gatsby';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';

import {
  SEO,
  Image,
  MarkdownContent,
  TeamMemberV2,
  OutboundLink,
  InternalLink,
  StaffMemberSchema,
  BreadcrumbListSchema,
  WebsiteComponentInteraction,
  EVENT_CATEGORIES,
  EVENT_ACTIONS,
  EVENT_LABELS,
  Email,
  Phone,
  FadeReveal,
  BasicCarousel,
  componentIterator,
  HorizontalImageCard,
  PriceRange,
  Section,
  ScrollableContent,
  MultipleComponentIterator,
  FullImageCard,
  FishermanIcon,
} from '@bluefin/components';

import { createServiceItemSlug, createTeamMemberSlug } from '../../index';
import {
  getPageByType,
  getBookNowPage,
  getCtaInteractions,
  getBusinessFiles,
  getTeamMemberServices,
  getComponentContentNodeContent,
  getFirstBusinessFile,
  generateDefaultHeadshotAltText,
} from '../../template-utils';
import { constructFullPageUrl, ensureSlugSlashes, getPageUrl, slugify } from '../../utils';

export default function TeamMemberTemplate({ data, pageContext }) {
  const {
    fishermanBusinessTeam: teamMember = {},
    fishermanBusiness: { name: businessName, businessCategories = [], website },
    allFishermanBusinessWebsitePage: { nodes: allPages = [] } = {},
    fishermanBusinessWebsitePage: { components: contentNodeComponents = [] } = {},
    allFishermanBusinessTeam: { nodes: allTeamMembers = [] } = {},
    allFishermanBusinessFile: { nodes: allFiles = [] } = {},
    allFishermanBusinessMenuCategory: { nodes: allCategories = [] } = {},
    allFishermanBusinessMenuItem: { nodes: allItems = [] } = {},
    site: {
      siteMetadata: { baseUrl },
    },
  } = data;
  const {
    fishermanParentPageTitle = 'Team',
    fishermanParentPageSlug = '/team/',
    templateProps = {},
  } = pageContext;

  const { priceRangeProps = {} } = templateProps;

  const {
    _id,
    name,
    email,
    phone,
    description,
    role,
    gatsbyImage,
    image,
    socialMedia,
    locations = [],
    interactions = [],
    hours = [],
    files = [],
    associatedItemVariations = [],
    teams = [],
    seoTitle,
    seoDescription,
    seoOgTitle,
    seoOgDescription,
    seoOgImage,
  } = teamMember;

  const url = getPageUrl(website, [
    createTeamMemberSlug(teamMember, allTeamMembers, fishermanParentPageSlug),
  ]);

  function getOtherTeamMembers() {
    return allTeamMembers.filter(({ _id: otherTeamMemberId }) => otherTeamMemberId !== _id);
  }

  const bookingPage = getBookNowPage(allPages);
  const teamPage = getPageByType(allPages, 'Team');
  const otherTeamMembers = getOtherTeamMembers();
  const { slug: teamPageSlug } = teamPage || {};
  const filteredInteractions = getCtaInteractions(interactions);

  const headshot = gatsbyImage || image;
  const hasHeadshot = !_isNil(headshot);
  const teamMemberBusinessFiles = getBusinessFiles(files, allFiles);
  const hasImages = !_isEmpty(teamMemberBusinessFiles);

  const teamMemberServices = getTeamMemberServices(allCategories, associatedItemVariations);

  const serversHeaderContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'team_member_services_header',
  });
  const servicesDescriptionContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'team_member_services_description',
  });
  const otherTeamMembersHeaderContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'team_other_team_members_header',
  });
  const otherTeamMembersSubheaderContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'team_other_team_members_subheader',
  });
  const otherTeamMembersDescriptionContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'team_other_team_members_description',
  });
  const seeAllTeamButtonContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'see_all_team_button',
  });

  return (
    <>
      <SEO
        businessName={businessName}
        pageName={`${fishermanParentPageTitle} - ${name}`}
        title={seoTitle}
        description={seoDescription || description}
        truncateDescription
        images={hasHeadshot ? [image] : null}
        ogTitle={seoOgTitle}
        ogDescription={seoOgDescription}
        ogImage={seoOgImage}
        ogUrl={url}
      />
      <StaffMemberSchema
        businessName={businessName}
        businessCategories={businessCategories}
        name={name}
        jobTitle={role}
        description={description}
      />
      <BreadcrumbListSchema
        items={[
          {
            name: `${fishermanParentPageTitle} | ${businessName}`,
            url: constructFullPageUrl(baseUrl, ensureSlugSlashes(fishermanParentPageSlug)),
          },
          {
            name: `${fishermanParentPageTitle} - ${name} | ${businessName}`,
          },
        ]}
      />
      <div className={`team-member-page ${slugify(name)}`}>
        <Section className="info-section">
          <Breadcrumb>
            <Breadcrumb.Section
              link
              to={ensureSlugSlashes(fishermanParentPageSlug)}
              as={InternalLink}
            >
              {fishermanParentPageTitle}
            </Breadcrumb.Section>
            <Breadcrumb.Divider icon="right chevron" />
            <Breadcrumb.Section>{name}</Breadcrumb.Section>
          </Breadcrumb>
          <Grid stackable>
            {hasHeadshot && (
              <Grid.Column width={8} className="image-column">
                <FadeReveal triggerOnce>
                  <Image
                    src={headshot}
                    altText={generateDefaultHeadshotAltText(name, businessName)}
                    loadStrategy="eager"
                  />
                </FadeReveal>
              </Grid.Column>
            )}
            <Grid.Column width={8} className="content-column">
              <div>
                <Header as="h1">{name}</Header>
                <Header as="h2">{role}</Header>
                {!_isEmpty(socialMedia) && (
                  <TeamMemberV2.Social className="social" social={socialMedia} showHandle />
                )}
                {!_isEmpty(teams) && (
                  <div className="teams">
                    {teams.map(({ _id: teamId, name: teamName }) => (
                      <Label key={teamId} className="inverted" content={teamName} />
                    ))}
                  </div>
                )}
                {!_isEmpty(locations) && (
                  <div className="locations">
                    {locations.map(({ _id: id, name: locationName }) => (
                      <Label key={id}>{locationName}</Label>
                    ))}
                  </div>
                )}
                <MarkdownContent className="description">{description}</MarkdownContent>
                <div className="ctas-container">
                  {bookingPage && _isEmpty(filteredInteractions) && (
                    <Button
                      primary
                      as={OutboundLink}
                      to={bookingPage.url}
                      event={{
                        category: EVENT_CATEGORIES.BOOKING_EVENT_CATEGORY,
                        action: EVENT_ACTIONS.BOOKING_SCHEDULE_APPOINTMENT_INTENT_EVENT_ACTION,
                      }}
                    >
                      {bookingPage.title}
                    </Button>
                  )}
                  {filteredInteractions.map((interaction) => {
                    const { _id: id } = interaction;
                    return (
                      <WebsiteComponentInteraction
                        key={id}
                        interaction={interaction}
                        displayComponentPropsMap={{
                          button: {
                            size: null,
                            compact: false,
                          },
                        }}
                        event={{
                          category: EVENT_CATEGORIES.TEAM_EVENT_CATEGORY,
                          action: EVENT_ACTIONS.TEAM_MEMBER_INTERACTION_ACTION,
                        }}
                      />
                    );
                  })}
                </div>
              </div>
              <div>
                {!_isEmpty(email) && <Email email={email} />}
                {!_isEmpty(phone) && <Phone phone={phone} ariaLabel={`Call ${name}`} />}
                <TeamMemberV2.Schedule hours={hours} fullDayName />
              </div>
            </Grid.Column>
          </Grid>
        </Section>
        {hasImages && (
          <Section className="images-section">
            <FadeReveal triggerOnce>
              <BasicCarousel
                size={3}
                displayIndicators={false}
                displayPlayPauseButton
                autoScroll
                slide
                leftArrowIcon="arrow left"
                rightArrowIcon="arrow right"
                items={componentIterator({
                  iterator: teamMemberBusinessFiles,
                  component: <Image background />,
                  propMap: { src: 'src', altText: 'altText' },
                })}
              />
            </FadeReveal>
          </Section>
        )}
        {!_isEmpty(teamMemberServices) && (
          <Section className="services-section">
            {serversHeaderContent && <Header as="h2">{serversHeaderContent}</Header>}
            {servicesDescriptionContent && (
              <MarkdownContent>{servicesDescriptionContent}</MarkdownContent>
            )}
            <Card.Group className="category-items" itemsPerRow={2}>
              {teamMemberServices.map(({ items }) =>
                items.map((item) => {
                  const imageFile = getFirstBusinessFile(item.files, allFiles);

                  return (
                    <FadeReveal key={item._id} className="ui card" triggerOnce>
                      <HorizontalImageCard
                        as={InternalLink}
                        link
                        event={{
                          category: 'Services',
                          action: 'View Item Details',
                          label: item.name,
                        }}
                        imagePosition="right"
                        image={imageFile && imageFile.src}
                        to={createServiceItemSlug(item, allItems, allCategories)}
                      >
                        <Card.Header className="header-container">
                          <Card.Header content={item.name} />
                          <PriceRange
                            prices={item.variations}
                            priceKey="price"
                            settings={{
                              displayType: 'range',
                              displayZeroCost: false,
                            }}
                            {...(priceRangeProps || {})}
                          />
                        </Card.Header>
                        <MarkdownContent children={item.description} />
                        <div className="button-container">
                          <Label content="See Details" />
                          <FishermanIcon iconName="arrow-right" />
                        </div>
                      </HorizontalImageCard>
                    </FadeReveal>
                  );
                }),
              )}
            </Card.Group>
          </Section>
        )}
        {!_isEmpty(otherTeamMembers) && (
          <Section className="team-section">
            <ScrollableContent direction="horizontal" distanceFactor="snap" intervalFactor={500}>
              <Grid stackable columns={2}>
                <Grid.Column>
                  {otherTeamMembersHeaderContent && (
                    <Header as="h2">{otherTeamMembersHeaderContent}</Header>
                  )}
                  {otherTeamMembersSubheaderContent && (
                    <Header as="h3">{otherTeamMembersSubheaderContent}</Header>
                  )}
                  {otherTeamMembersDescriptionContent && (
                    <MarkdownContent>{otherTeamMembersDescriptionContent}</MarkdownContent>
                  )}

                  <Button
                    as={InternalLink}
                    to={ensureSlugSlashes(teamPageSlug)}
                    content={seeAllTeamButtonContent || 'See Our Team'}
                    className="see-more-button"
                    primary
                    event={{
                      category: EVENT_CATEGORIES.TEAM_EVENT_CATEGORY,
                      action: EVENT_ACTIONS.TEAM_VIEW_ALL_MEMBERS_EVENT_ACTION,
                      label: EVENT_LABELS.TEAM_MEMBER_LABEL,
                    }}
                  />

                  <div className="triggers-container">
                    <ScrollableContent.PreviousTrigger />
                    <ScrollableContent.NextTrigger />
                  </div>
                </Grid.Column>
                <Grid.Column>
                  <ScrollableContent.Content hideScrollbar>
                    <MultipleComponentIterator
                      iterator={otherTeamMembers}
                      components={[
                        {
                          component: <FadeReveal className="ui card" triggerOnce />,
                          propMap: { key: '_id' },
                          children: [
                            {
                              component: (
                                <FullImageCard
                                  link
                                  className="team-member-card"
                                  as={InternalLink}
                                  event={{
                                    category: 'Team',
                                    action: 'View Team Member',
                                  }}
                                />
                              ),
                              propMap: {
                                key: '_id',
                                image: 'gatsbyImage',
                                to: createTeamMemberSlug,
                              },
                              children: [
                                {
                                  component: <TeamMemberV2.Name />,
                                  propMap: { name: 'name' },
                                },
                                {
                                  component: <TeamMemberV2.Role />,
                                  propMap: { role: 'role' },
                                },
                              ],
                            },
                          ],
                        },
                      ]}
                    />
                  </ScrollableContent.Content>
                  <div className="actions-container">
                    <Button
                      as={InternalLink}
                      to={ensureSlugSlashes(teamPageSlug)}
                      content={seeAllTeamButtonContent || 'See Our Team'}
                      className="see-more-button"
                      primary
                      event={{
                        category: EVENT_CATEGORIES.TEAM_EVENT_CATEGORY,
                        action: EVENT_ACTIONS.TEAM_VIEW_ALL_MEMBERS_EVENT_ACTION,
                        label: EVENT_LABELS.TEAM_MEMBER_LABEL,
                      }}
                    />
                    <div className="triggers-container">
                      <ScrollableContent.PreviousTrigger />
                      <ScrollableContent.NextTrigger />
                    </div>
                  </div>
                </Grid.Column>
              </Grid>
            </ScrollableContent>
          </Section>
        )}
      </div>
    </>
  );
}

export const query = graphql`
  query ($id: Int!) {
    site {
      siteMetadata {
        baseUrl
      }
    }
    fishermanBusinessTeam(_id: { eq: $id }) {
      _id
      name
      email
      phone
      description
      role
      image
      gatsbyImage {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      socialMedia {
        platform
        url
      }
      locations {
        _id
        name
      }
      hours {
        startDay
        openTime
        closeTime
      }
      interactions {
        behaviorType
        displayType
        enabled
        label
        url
        style
      }
      files
      associatedItemVariations {
        itemId
        variationId
        price
        item {
          _id
          name
          description
          variations {
            _id
            name
            order
            price
          }
          files
          visible
        }
      }
      teams {
        _id
        name
      }
      seoTitle
      seoDescription
      seoOgTitle
      seoOgDescription
      seoOgImage {
        url
        alt
        type
      }
    }
    fishermanBusiness {
      name
      businessCategories {
        category
      }
      website {
        domains {
          url
          primaryDomain
        }
      }
    }
    allFishermanBusinessWebsitePage {
      nodes {
        url
        title
        pageType
        slug
      }
    }
    fishermanBusinessWebsitePage(pageType: { eq: "Team" }) {
      components {
        componentIdentifier
        contentNodes {
          content
        }
      }
    }
    allFishermanBusinessTeam(sort: { order: ASC, fields: order }, filter: { show: { eq: true } }) {
      nodes {
        _id
        name
        role
        image
        gatsbyImage {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
        show
      }
    }
    allFishermanBusinessFile {
      nodes {
        _id
        file
        gatsbyFile {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
        description
      }
    }
    allFishermanBusinessMenuCategory(sort: { order: ASC, fields: order }) {
      nodes {
        _id
        name
        items
      }
    }
    allFishermanBusinessMenuItem {
      nodes {
        _id
        name
      }
    }
  }
`;
